import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  ArrowUp,
  ArrowDown,
  CalendarDays,
  ExternalLink,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Checkbox,
  Button,
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import NotesDialog from './NotesDialog';
import TaskDocumentsPopover from '../documents/TaskDocumentsPopover';
import { Loader2, AlertCircle } from 'lucide-react';
import { DateTime } from 'luxon';
import { getOrderManagerItems } from '../../services/api';
import type { OrderManagerItemResponse } from '../../services/api/types.gen';
import {
  formatDateInAgencyZone,
  formatDateToZeroPaddedMMDDYYYY,
} from '../../utils/dateUtils';
import { formatOrderDeliveryPreference } from '../../utils/orderDeliveryPreference';
import { orderRowDestination, patientChartPath } from '../../utils/navigation';
import { ReportLink } from '../reporting/ReportLink';
import { STATUS_DISPLAY_LABELS } from '../task/taskStatus';
import { PaginationBar } from '../payroll/PaginationBar';
import OrderManagerActions from './OrderManagerActions';
import EditDatesModal from './EditDatesModal';

const DEFAULT_PER_PAGE = 25;

export const DELETED_PHYSICIAN_TOOLTIP =
  'This physician was removed from the directory. The record still shows who was assigned.';

// The order keeps naming the physician it was assigned to; this marks that
// they have since been removed from the directory.
const DeletedPhysicianBadge: React.FC = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className='cursor-help'
          onClick={e => e.stopPropagation()}
        >
          <Badge
            variant='secondary'
            className='whitespace-nowrap text-muted-foreground'
          >
            Deleted
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent className='max-w-[260px]'>
        {DELETED_PHYSICIAN_TOOLTIP}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

type OrderManagerTabId =
  'not-ready' | 'to-be-sent' | 'pending-signature' | 'received';

type SortField =
  | 'patient_name'
  | 'order_type'
  | 'order_date'
  | 'order_physician_name'
  | 'sent_to_physician_date'
  | 'received_date';
type SortDir = 'asc' | 'desc';

const STATUS_MAP: Record<OrderManagerTabId, string> = {
  'not-ready': 'not_ready',
  'to-be-sent': 'completed',
  'pending-signature': 'sent_to_physician',
  received: 'returned_with_signature',
};

// Soft, informational highlight for a deep-linked order row (a neutral purple
// tint, not the red validation/error ring). Applied to the row, then faded out.
const ORDER_HIGHLIGHT_CLASSES = ['bg-violet-100', 'transition-colors'];
const ORDER_HIGHLIGHT_DURATION_MS = 2000;

// Whole days between a UTC datetime and now. Only valid for backend
// "timestamp with time zone" fields, which are emitted as UTC ISO strings.
function daysSince(utcDateTime: string | null | undefined): string {
  if (!utcDateTime) return '-';
  const dt = DateTime.fromISO(utcDateTime, { zone: 'utc' });
  if (!dt.isValid) return '-';
  const days = Math.ceil((Date.now() - dt.toMillis()) / (1000 * 60 * 60 * 24));
  return `${days}d`;
}

export type OrderManagerListFilterQuery = {
  hide_imported_patients?: boolean;
  hide_test?: boolean;
  patient_search?: string;
  clinic_id?: string;
  order_type?: string;
  clinic_physician_id?: string;
  unassigned_physician?: boolean;
  order_date_from?: string;
  order_date_to?: string;
};

interface OrderManagerTabProps {
  tabId: OrderManagerTabId;
  refetchKey?: number;
  onAfterRefetch?: () => void;
  highlightOrderId?: string;
  filterQuery?: OrderManagerListFilterQuery;
  /** When true, empty copy names filters rather than an empty stage. */
  hasNarrowingFilters?: boolean;
}

const OrderManagerTab: React.FC<OrderManagerTabProps> = ({
  tabId,
  refetchKey,
  onAfterRefetch,
  highlightOrderId,
  filterQuery,
  hasNarrowingFilters = false,
}) => {
  const onAfterRefetchRef = useRef(onAfterRefetch);
  onAfterRefetchRef.current = onAfterRefetch;
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderManagerItemResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('order_date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [editNotesOrder, setEditNotesOrder] =
    useState<OrderManagerItemResponse | null>(null);
  const [editDatesOrder, setEditDatesOrder] =
    useState<OrderManagerItemResponse | null>(null);

  const highlightedRef = useRef<string | null>(null);
  // Deep-link: ask the API once for the page that contains this order, then
  // stop sending focus_order_id so normal paging/sort is not overridden.
  const pendingFocusOrderIdRef = useRef<string | undefined>(highlightOrderId);
  // Drop stale responses when a newer page/sort/filter fetch is already going.
  const requestIdRef = useRef(0);
  // When focus_order_id returns a different page, we already have that page's
  // rows — skip the immediate re-fetch that setPage would otherwise trigger.
  const skipNextFetchRef = useRef(false);
  useEffect(() => {
    pendingFocusOrderIdRef.current = highlightOrderId;
    highlightedRef.current = null;
  }, [highlightOrderId]);

  useEffect(() => {
    if (!highlightOrderId || loading) return;
    if (highlightedRef.current === highlightOrderId) return; // one-shot
    // Off-page or wrong-tab until focus_order_id lands: wait for the row.
    if (!orders.some(o => o.id === highlightOrderId)) return;
    const el = document.querySelector<HTMLElement>(
      `[data-order-id="${highlightOrderId}"]`
    );
    if (!el) return;
    highlightedRef.current = highlightOrderId;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add(...ORDER_HIGHLIGHT_CLASSES);
    const t = window.setTimeout(
      () => el.classList.remove('bg-violet-100'),
      ORDER_HIGHLIGHT_DURATION_MS
    );
    return () => window.clearTimeout(t);
  }, [highlightOrderId, loading, orders]);

  const fetchOrders = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    const focusOrderId = pendingFocusOrderIdRef.current;
    try {
      // The generated client resolves HTTP failures into `{ error }` instead
      // of throwing, so a failed load must be checked for explicitly or it
      // would render as an empty queue.
      const { data, error: requestError } = await getOrderManagerItems({
        query: {
          status: STATUS_MAP[tabId],
          hide_imported_patients: true,
          hide_test: true,
          ...filterQuery,
          page,
          per_page: DEFAULT_PER_PAGE,
          sort_by: sortField,
          sort_order: sortDir,
          ...(focusOrderId ? { focus_order_id: focusOrderId } : {}),
        },
      });
      // A newer fetch started while this was in flight — ignore this result.
      if (requestId !== requestIdRef.current) return;
      // A success whose body is not a page of orders (an API that predates or
      // postdates this client) is a failed load too, never an empty queue.
      const isOrdersPage = !!data && Array.isArray(data.items);
      if (requestError || !isOrdersPage) {
        console.error(
          'Failed to load orders',
          requestError ?? 'Unexpected response shape'
        );
        setOrders([]);
        setTotal(0);
        setTotalPages(1);
        setError('Failed to load orders');
        return;
      }
      const items = data.items;
      const nextTotal = data.total ?? 0;
      const nextTotalPages = data.total_pages ?? 1;
      const nextPage = data.page ?? page;

      // Consume focus after the first response so later page changes/sorts
      // are not snapped back to the deep-linked order.
      if (focusOrderId) {
        pendingFocusOrderIdRef.current = undefined;
      }

      // Payroll-style clamp: a refetch can land past the last page after a
      // bulk action shrinks the filtered set.
      if (page > 1 && items.length === 0 && nextTotal > 0) {
        setPage(nextTotalPages);
        return;
      }

      setOrders(items);
      setTotal(nextTotal);
      setTotalPages(nextTotalPages);
      if (nextPage !== page) {
        skipNextFetchRef.current = true;
        setPage(nextPage);
      }
      onAfterRefetchRef.current?.();
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error('Failed to load orders', err);
      setError('Failed to load orders');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [filterQuery, page, sortDir, sortField, tabId]);

  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    fetchOrders();
  }, [fetchOrders, refetchKey]);

  // Selection is page-scoped: prune ids that left the visible page.
  useEffect(() => {
    const visibleIds = new Set(orders.map(order => order.id));
    setSelectedIds(prev => {
      const next = new Set(
        Array.from(prev).filter(orderId => visibleIds.has(orderId))
      );
      return next.size === prev.size ? prev : next;
    });
  }, [orders]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(orders.map(o => o.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOrder = (orderId: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(orderId);
      else next.delete(orderId);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (loading) return;
    setPage(1);
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortHeaderClassName = loading
    ? 'select-none text-muted-foreground'
    : 'cursor-pointer select-none';

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? (
      <ArrowUp className='ml-1 inline h-3 w-3' />
    ) : (
      <ArrowDown className='ml-1 inline h-3 w-3' />
    );
  };

  const showSentColumns = tabId === 'pending-signature' || tabId === 'received';
  const showAgeColumn = tabId === 'pending-signature';
  const showReceivedColumn = tabId === 'received';
  // A Not Ready order still has work left on its task, so the row names the
  // status it is waiting in and offers the way to the task.
  const showTaskColumns = tabId === 'not-ready';
  const selectedOrders = orders.filter(order => selectedIds.has(order.id));

  // Initial load only: keep the table mounted on page/sort refetches (Payroll).
  if (loading && orders.length === 0 && !error) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (error && orders.length === 0) {
    return (
      <div className='flex items-center justify-center gap-2 py-12 text-destructive'>
        <AlertCircle className='h-5 w-5' />
        <span>{error}</span>
      </div>
    );
  }

  if (!loading && orders.length === 0) {
    return (
      <div className='py-12 text-center text-muted-foreground'>
        {hasNarrowingFilters
          ? 'No orders match the current filters'
          : 'No orders in this stage'}
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className='w-10'>
              <Checkbox
                checked={
                  selectedIds.size === orders.length && orders.length > 0
                }
                onCheckedChange={handleSelectAll}
              />
            </TableHead>
            <TableHead>Order #</TableHead>
            <TableHead
              className={sortHeaderClassName}
              aria-disabled={loading || undefined}
              onClick={() => handleSort('patient_name')}
            >
              Patient <SortIcon field='patient_name' />
            </TableHead>
            <TableHead
              className={sortHeaderClassName}
              aria-disabled={loading || undefined}
              onClick={() => handleSort('order_type')}
            >
              Type <SortIcon field='order_type' />
            </TableHead>
            <TableHead
              className={sortHeaderClassName}
              aria-disabled={loading || undefined}
              onClick={() => handleSort('order_physician_name')}
            >
              Physician <SortIcon field='order_physician_name' />
            </TableHead>
            <TableHead>Delivery Pref.</TableHead>
            <TableHead
              className={sortHeaderClassName}
              aria-disabled={loading || undefined}
              onClick={() => handleSort('order_date')}
            >
              Order Date <SortIcon field='order_date' />
            </TableHead>
            {showTaskColumns && <TableHead>Status</TableHead>}
            {showSentColumns && (
              <>
                <TableHead
                  className={sortHeaderClassName}
                  aria-disabled={loading || undefined}
                  onClick={() => handleSort('sent_to_physician_date')}
                >
                  Sent Date <SortIcon field='sent_to_physician_date' />
                </TableHead>
                {showAgeColumn && <TableHead>Age</TableHead>}
              </>
            )}
            {showReceivedColumn && (
              <TableHead
                className={sortHeaderClassName}
                aria-disabled={loading || undefined}
                onClick={() => handleSort('received_date')}
              >
                Received <SortIcon field='received_date' />
              </TableHead>
            )}
            {showReceivedColumn && <TableHead>Phys. Sign Date</TableHead>}
            <TableHead className='w-10'>Docs</TableHead>
            <TableHead className='w-10'>Notes</TableHead>
            {showTaskColumns && <TableHead>Task</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map(order => (
            <TableRow
              key={order.id}
              data-order-id={order.id}
              className='cursor-pointer'
              onClick={() => navigate(orderRowDestination(order))}
            >
              <TableCell onClick={e => e.stopPropagation()}>
                <Checkbox
                  checked={selectedIds.has(order.id)}
                  onCheckedChange={(checked: boolean) =>
                    handleSelectOrder(order.id, !!checked)
                  }
                />
              </TableCell>
              <TableCell>{order.id.slice(0, 8)}</TableCell>
              <TableCell>
                <ReportLink to={patientChartPath(order.patient_id)}>
                  {order.patient_name}
                </ReportLink>
              </TableCell>
              <TableCell>{order.order_type_display_name}</TableCell>
              <TableCell>
                <span className='flex flex-wrap items-center gap-2'>
                  {order.order_physician_name}
                  {order.physician_deleted && <DeletedPhysicianBadge />}
                </span>
              </TableCell>
              <TableCell>
                {formatOrderDeliveryPreference(
                  order.physician_delivery_preference
                ) ?? '—'}
              </TableCell>
              <TableCell>
                {formatDateToZeroPaddedMMDDYYYY(order.order_date) || '-'}
              </TableCell>
              {showTaskColumns && (
                <TableCell>
                  {STATUS_DISPLAY_LABELS[order.status] ?? order.status}
                </TableCell>
              )}
              {showSentColumns && (
                <>
                  <TableCell>
                    {formatDateInAgencyZone(order.sent_to_physician_date) ||
                      '-'}
                  </TableCell>
                  {showAgeColumn && (
                    <TableCell>
                      {daysSince(order.sent_to_physician_date)}
                    </TableCell>
                  )}
                </>
              )}
              {showReceivedColumn && (
                <TableCell>
                  {(order.signature_required &&
                    formatDateInAgencyZone(order.received_date)) ||
                    '—'}
                </TableCell>
              )}
              {showReceivedColumn && (
                <TableCell>
                  {(order.signature_required &&
                    formatDateInAgencyZone(order.physician_sign_date)) ||
                    '—'}
                </TableCell>
              )}
              <TableCell onClick={e => e.stopPropagation()}>
                <TaskDocumentsPopover
                  taskId={order.id}
                  patientId={order.patient_id}
                  initialCount={order.document_count}
                  targetLabel={`${order.order_type_display_name} · ${order.patient_name}`}
                  onDocumentsChanged={fetchOrders}
                />
              </TableCell>
              <TableCell onClick={e => e.stopPropagation()}>
                <div className='flex items-center gap-1'>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    aria-label={`Edit notes for ${order.order_type_display_name}`}
                    onClick={() => setEditNotesOrder(order)}
                  >
                    <MessageSquare
                      className={
                        order.notes
                          ? 'text-blue-500'
                          : 'text-muted-foreground/30'
                      }
                    />
                  </Button>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    aria-label={`Edit ${order.order_type_display_name}`}
                    onClick={() => setEditDatesOrder(order)}
                  >
                    <CalendarDays className='text-muted-foreground/50' />
                  </Button>
                </div>
              </TableCell>
              {showTaskColumns && (
                <TableCell onClick={e => e.stopPropagation()}>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    aria-label={`Open task for ${order.order_type_display_name}`}
                    onClick={() => navigate(orderRowDestination(order))}
                  >
                    <ExternalLink className='mr-1.5 h-4 w-4' />
                    Open task
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {total > 0 && (
        <div className='mt-4'>
          <PaginationBar
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={DEFAULT_PER_PAGE}
            itemsLabel='orders'
            onPageChange={setPage}
            disabled={loading}
          />
        </div>
      )}

      <OrderManagerActions
        activeTab={tabId}
        selectedOrders={selectedOrders}
        onClearSelection={() => setSelectedIds(new Set())}
        onActionComplete={fetchOrders}
      />

      <NotesDialog
        open={!!editNotesOrder}
        onOpenChange={open => {
          if (!open) setEditNotesOrder(null);
        }}
        taskIds={editNotesOrder ? [editNotesOrder.id] : []}
        initialNotes={editNotesOrder?.notes ?? ''}
        mode='single'
        onSuccess={fetchOrders}
      />

      {editDatesOrder && (
        <EditDatesModal
          open={!!editDatesOrder}
          onOpenChange={open => {
            if (!open) setEditDatesOrder(null);
          }}
          orders={[editDatesOrder]}
          activeTab={tabId}
          onSuccess={fetchOrders}
        />
      )}
    </>
  );
};

export default OrderManagerTab;
